import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Editor from '@monaco-editor/react';
import './GhostCoding.css';

const GhostCoding = ({ questionId, isOpen, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [playbackData, setPlaybackData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentCode, setCurrentCode] = useState('');
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && questionId) {
      fetchTopSessions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, questionId]);

  const fetchTopSessions = async () => {
    try {
      const response = await api.get(`/api/ghost-coding/questions/${questionId}/top?limit=10`);
      setSessions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ghost sessions:', error);
      setLoading(false);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const response = await api.get(`/api/ghost-coding/sessions/${sessionId}/playback`);
      setPlaybackData(response.data);
      setSelectedSession(sessionId);
      setCurrentCode('');
      setCurrentEventIndex(0);
      setIsPlaying(false);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const playSession = () => {
    if (!playbackData || !playbackData.events) return;

    setIsPlaying(true);
    let eventIndex = currentEventIndex;

    const playNextEvent = () => {
      if (eventIndex >= playbackData.events.length) {
        setIsPlaying(false);
        return;
      }

      const event = playbackData.events[eventIndex];
      
      if (event.action === 'type') {
        setCurrentCode(event.data);
      } else if (event.action === 'delete') {
        setCurrentCode(prev => prev.slice(0, -1));
      }

      eventIndex++;
      setCurrentEventIndex(eventIndex);

      // Schedule next event based on timestamp and speed
      const nextEvent = playbackData.events[eventIndex];
      if (nextEvent) {
        const delay = (nextEvent.timestamp - event.timestamp) / playbackSpeed;
        setTimeout(playNextEvent, Math.max(delay, 50)); // Minimum 50ms between events
      } else {
        setIsPlaying(false);
      }
    };

    playNextEvent();
  };

  const pauseSession = () => {
    setIsPlaying(false);
  };

  const resetSession = () => {
    setCurrentCode('');
    setCurrentEventIndex(0);
    setIsPlaying(false);
  };

  const likeSession = async (sessionId) => {
    try {
      await api.post(`/api/ghost-coding/sessions/${sessionId}/like`);
      // Refresh sessions to update like count
      fetchTopSessions();
    } catch (error) {
      console.error('Error liking session:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ghost-coding-modal-overlay" onClick={onClose}>
      <div className="ghost-coding-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ghost-coding-header">
          <h2>👻 Ghost Coding - Learn from the Best</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="ghost-coding-content">
          {/* Sessions List */}
          <div className="sessions-sidebar">
            <h3>🏆 Top Solutions</h3>
            {loading ? (
              <div className="loading-text">Loading sessions...</div>
            ) : (
              <div className="sessions-list">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`session-item ${selectedSession === session.id ? 'active' : ''}`}
                    onClick={() => loadSession(session.id)}
                  >
                    <div className="session-user">
                      <span className="user-icon">👤</span>
                      <span className="username">{session.username}</span>
                    </div>
                    <div className="session-stats">
                      <div className="stat">
                        <span className="stat-icon">✨</span>
                        <span>{session.codeSmellScore?.toFixed(0) || 'N/A'}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-icon">⚡</span>
                        <span>{session.executionTimeMs || 'N/A'}ms</span>
                      </div>
                      <div className="stat">
                        <span className="stat-icon">❤️</span>
                        <span>{session.likesCount || 0}</span>
                      </div>
                    </div>
                    <div className="session-language">{session.language}</div>
                  </div>
                ))}
                {sessions.length === 0 && (
                  <div className="no-sessions">
                    <p>No recorded sessions yet.</p>
                    <p>Be the first to solve this problem!</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Playback Area */}
          <div className="playback-area">
            {playbackData ? (
              <>
                <div className="playback-info">
                  <div className="info-item">
                    <span className="info-label">Coder:</span>
                    <span className="info-value">{playbackData.username}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Duration:</span>
                    <span className="info-value">{playbackData.duration}s</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Quality:</span>
                    <span className="info-value score">
                      {playbackData.codeSmellScore?.toFixed(0) || 'N/A'}/100
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Result:</span>
                    <span className={`info-value ${playbackData.isSuccessful ? 'success' : 'fail'}`}>
                      {playbackData.isSuccessful ? '✅ Success' : '❌ Failed'}
                    </span>
                  </div>
                </div>

                <div className="editor-container">
                  <Editor
                    height="400px"
                    language={playbackData.language || 'javascript'}
                    value={currentCode}
                    theme="vs-dark"
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      fontSize: 14,
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false
                    }}
                  />
                </div>

                <div className="playback-controls">
                  <div className="control-buttons">
                    {!isPlaying ? (
                      <button className="control-btn play" onClick={playSession}>
                        ▶ Play
                      </button>
                    ) : (
                      <button className="control-btn pause" onClick={pauseSession}>
                        ⏸ Pause
                      </button>
                    )}
                    <button className="control-btn reset" onClick={resetSession}>
                      ⏮ Reset
                    </button>
                  </div>

                  <div className="speed-control">
                    <label>Speed:</label>
                    <button 
                      className={`speed-btn ${playbackSpeed === 0.5 ? 'active' : ''}`}
                      onClick={() => setPlaybackSpeed(0.5)}
                    >
                      0.5x
                    </button>
                    <button 
                      className={`speed-btn ${playbackSpeed === 1 ? 'active' : ''}`}
                      onClick={() => setPlaybackSpeed(1)}
                    >
                      1x
                    </button>
                    <button 
                      className={`speed-btn ${playbackSpeed === 2 ? 'active' : ''}`}
                      onClick={() => setPlaybackSpeed(2)}
                    >
                      2x
                    </button>
                    <button 
                      className={`speed-btn ${playbackSpeed === 5 ? 'active' : ''}`}
                      onClick={() => setPlaybackSpeed(5)}
                    >
                      5x
                    </button>
                  </div>

                  <div className="progress-info">
                    Event: {currentEventIndex} / {playbackData.events?.length || 0}
                  </div>
                </div>

                <div className="action-buttons">
                  <button 
                    className="like-btn"
                    onClick={() => likeSession(selectedSession)}
                  >
                    ❤️ Like this solution
                  </button>
                </div>
              </>
            ) : (
              <div className="no-playback">
                <div className="ghost-icon">👻</div>
                <h3>Select a Ghost Session</h3>
                <p>Watch how top performers solved this problem</p>
                <p>Learn their coding patterns and strategies</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GhostCoding;


